import {
  AfterViewInit,
  ApplicationRef,
  ComponentRef,
  createComponent,
  Directive,
  ElementRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';
import { FormGroupDirective, NgControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { distinctUntilChanged, fromEvent, merge, startWith, Subject, takeUntil, tap } from 'rxjs';

import { ControlErrorComponent } from './control-error/control-error.component';

@Directive({
  selector: '[rmsControlError]',
})
export class ControlErrorDirective implements AfterViewInit, OnDestroy {
  @Input() public label: string | null = null;

  private readonly formGroupDirective = inject(FormGroupDirective);
  private readonly injector = inject(Injector);
  private readonly appRef = inject(ApplicationRef);
  private readonly elementRef = inject(ElementRef);
  private readonly ngControl = inject(NgControl);
  private readonly matcher = inject(ErrorStateMatcher);
  private readonly vcr = inject(ViewContainerRef);

  private readonly destroy$ = new Subject<void>();

  private errorComponentRef: ComponentRef<ControlErrorComponent> | null = null;
  private labelElement: string | null = null;

  ngAfterViewInit() {
    if (!this.ngControl?.control) return;

    this.createErrorComponent();

    merge(
      fromEvent(this.elementRef.nativeElement, 'focusout'),
      this.ngControl.control.valueChanges,
      this.ngControl.control.statusChanges,
      this.formGroupDirective.ngSubmit
    )
      .pipe(
        startWith(this.ngControl.value),
        distinctUntilChanged(),
        tap(() => {
          if (!this.errorComponentRef) {
            return;
          }
          this.errorComponentRef.instance.label =
            this.label ?? this.labelElement?.replace(/\*/g, '') ?? this.ngControl.name?.toString() ?? '';
          this.errorComponentRef.instance.errors = this.ngControl.control?.errors;
          this.errorComponentRef.instance.errorState = this.matcher.isErrorState(
            this.ngControl.control,
            this.formGroupDirective
          );
          this.errorComponentRef.instance.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.errorComponentRef) {
      this.appRef.detachView(this.errorComponentRef.hostView);
      this.errorComponentRef.destroy();
    }
  }

  /**
   * Creates and inserts an error component (`ControlErrorComponent`) in the correct location.
   *
   * 1. If the element (`elementRef.nativeElement`) does not contain `mat-` classes,
   *    it indicates that the element is not inside a `mat-form-field`. I.g. Custom component with CVA
   *    In this case, we create the error component using `ViewContainerRef`.
   *
   * 2. If the element contains `mat-` classes (indicating it's inside a `mat-form-field`)
   *    i.g. angular material form components.
   *    We traverse upwards in the DOM until we find the first <div>` element that does not have `mat-` classes.
   *    i.g. our wrapper `div` for control group – `<div> <label /> <mat-form-field> ... </div>`
   *
   * 3. Once we find a suitable container, we create the error component and insert it inside.
   */
  private createErrorComponent() {
    if (!this.elementRef.nativeElement.className.includes('mat')) {
      this.errorComponentRef = this.vcr.createComponent(ControlErrorComponent);
      this.labelElement = this.elementRef.nativeElement.parentElement?.querySelector('label')?.textContent;

      return;
    }

    let parentContainer = this.elementRef.nativeElement.parentElement;

    while (parentContainer && (parentContainer.tagName !== 'DIV' || parentContainer.className.includes('mat'))) {
      parentContainer = parentContainer.parentElement;
    }

    if (!parentContainer) {
      return;
    }

    this.labelElement = parentContainer.querySelector('label')?.textContent;

    this.errorComponentRef = createComponent(ControlErrorComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    this.appRef.attachView(this.errorComponentRef.hostView);
    parentContainer.appendChild(this.errorComponentRef.location.nativeElement);
  }
}
